/* eslint-disable no-nested-ternary */
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  TableContainer,
  TableSortLabel,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  CircularProgress,
  TablePagination,
  Typography,
  Box,
  Grid,
} from "@material-ui/core";
import getUniquId from "utils/getUniqueId";
import translate from "../../../i18n/translate";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis?.map((el) => el[0]);
}

const SrTable = ({
  columns,
  rows,
  defaultRowsPerPage = 5,
  noPagination = false,
  filter = [],
  loading = false,
  noDataMessage,
  ...props
}) => {
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState("asc");
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [page, setPage] = useState(0);

  const handleSort = (columnId, isSortable) => {
    const isAsc = orderBy === columnId && order === "asc";
    if (isSortable) {
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(() => (order === "desc" ? null : columnId));
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleFilter = (nonFilteredRow) => {
    let fiteredRows = nonFilteredRow;
    if (!filter?.length) {
      return nonFilteredRow;
    }
    filter.forEach(({ id, value }) => {
      fiteredRows = fiteredRows.filter((row) => row?.[id] === value);
    });
    return fiteredRows;
  };

  const filteredRows = handleFilter(rows);

  return (
    <TableContainer {...props}>
      {loading ? (
        <Box display="flex" borderRadius="4px" bgcolor="white">
          <CircularProgress />
        </Box>
      ) : (
        <Table aria-label="customized table">
          <TableHead>
            <TableRow>
              {columns.map((column, index) => {
                const key = `header-${index}`;
                return (
                  <TableCell
                    key={key}
                    style={{ minWidth: column.minWidth }}
                    align={column.align ?? "inherit"}
                  >
                    {column.isSortable ? (
                      <TableSortLabel
                        active={!!column.isSortable && orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={() => handleSort(column.id, column.isSortable)}
                        hideSortIcon
                      >
                        {column.header}
                      </TableSortLabel>
                    ) : (
                      column.header
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          {rows?.length ? (
            <TableBody>
              {stableSort(
                filteredRows.slice(
                  noPagination ? 0 : page * rowsPerPage,
                  noPagination
                    ? filteredRows.length
                    : page * rowsPerPage + rowsPerPage
                ),
                getComparator(order, orderBy)
              ).map((row) => (
                <TableRow key={getUniquId("table_row")}>
                  {columns.map(({ id, align, cell, minWidth }) => (
                    <TableCell
                      style={{ minWidth }}
                      key={getUniquId("table_col")}
                      align={align ?? "inherit"}
                    >
                      {cell ? cell?.(row) : row?.[id]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableCell colSpan={2}>
              <Grid container item justifyContent="center">
                <Box padding={1} justifySelf="center">
                  <Typography variant="h3">
                    {noDataMessage || translate("common.noData")}
                  </Typography>
                </Box>
              </Grid>
            </TableCell>
          )}
        </Table>
      )}

      {!noPagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 75, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          labelRowsPerPage={translate("common.rowsPerPage")}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </TableContainer>
  );
};

SrTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
      id: PropTypes.string,
      isSortable: PropTypes.bool,
      cell: PropTypes.func,
      align: PropTypes.oneOf(["initial", "justify", "right", "left", "center"]),
      minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  defaultRowsPerPage: PropTypes.oneOf([5, 10, 25, 50, 75, 100]),
  noPagination: PropTypes.bool,
  filter: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      value: PropTypes.node,
    })
  ),
};

SrTable.defaultProps = {
  defaultRowsPerPage: 5,
  noPagination: false,
  filter: [],
};

export default SrTable;
